import React, { useId, useState } from 'react';
import { buildPlatformSlatRects } from 'besper-product-marks';
import { SlatMorphSvg } from './slatRaster/SlatMorphSvg';
import { SlatSilhouetteSvg } from './slatRaster/SlatSilhouetteSvg';
import { useSlatModelFromImage } from './slatRaster/useSlatModelFromImage';

const VB = 200;

/** Default high-contrast raster masks (public/diagrams). */
export const PLATFORM_RASTER_DEFAULT_SRC = `${process.env.PUBLIC_URL || ''}/diagrams/platform-core-mask.svg`;
export const PLATFORM_RASTER_HOVER_SRC = `${process.env.PUBLIC_URL || ''}/diagrams/europe-mask.png`;

/**
 * B-esper Platform hub diagram: default mask is a filled circle (horizontal slats = chords); on hover,
 * slats morph (split/merge, width changes) into Europe — single layer, no opacity crossfade.
 *
 * @param {object} props
 * @param {'raster' | 'mark'} [props.variant='raster']
 * @param {string} [props.rasterSrc] - mask image URL (same-origin recommended)
 * @param {string | null} [props.hoverRasterSrc]
 * @param {boolean} [props.morphOnHover=true]
 * @param {number} [props.rowCount] - internal scan rows (higher = more slat detail toward Europe)
 * @param {number} [props.barFillRatio]
 * @param {number} [props.padding]
 * @param {number} [props.threshold]
 * @param {number} [props.morphDurationMs]
 */
export function PlatformHubDiagramSvg({
  uid: _uid,
  variant = 'raster',
  rasterSrc = PLATFORM_RASTER_DEFAULT_SRC,
  hoverRasterSrc = PLATFORM_RASTER_HOVER_SRC,
  morphOnHover = true,
  rowCount = 24,
  barFillRatio = 0.5,
  padding = 6,
  threshold,
  morphDurationMs = 3200,
  ...svgAttrs
}) {
  const [isHovered, setIsHovered] = useState(false);
  const shouldMorph =
    variant === 'raster' && morphOnHover && Boolean(hoverRasterSrc) && hoverRasterSrc !== rasterSrc;

  const { rects } = useSlatModelFromImage(variant === 'raster' && !shouldMorph ? rasterSrc : null, {
    rowCount,
    barFillRatio,
    padding,
    threshold,
    viewBox: { width: VB, height: VB },
    smoothing: false,
  });

  if (variant === 'raster') {
    const hoverHandlers = shouldMorph
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : null;

    if (shouldMorph) {
      return (
        <SlatMorphSvg
          srcA={rasterSrc}
          srcB={hoverRasterSrc}
          active={isHovered}
          viewBoxSize={VB}
          dropShadow
          morphOptions={{
            rowCount,
            morphRowCountFrom: 16,
            morphMidRowCount: 3,
            morphRowCountTo: rowCount,
            twoPhaseMorph: true,
            analyticCircleSlats: { r: 58 },
            barFillRatio,
            padding,
            threshold,
            durationMs: morphDurationMs,
            smoothing: true,
          }}
          svgAttrs={{
            'aria-hidden': true,
            style: { cursor: 'pointer', pointerEvents: 'auto', ...svgAttrs.style },
            ...hoverHandlers,
            ...svgAttrs,
          }}
        />
      );
    }

    return (
      <SlatSilhouetteSvg
        rects={rects}
        viewBoxSize={VB}
        dropShadow
        svgAttrs={{ 'aria-hidden': true, ...svgAttrs }}
      />
    );
  }

  return <GeometricPlatformMarkSvg {...svgAttrs} />;
}

/** Classic geometric slats (taper, shear) for variant "mark" or non-raster use cases. */
export function GeometricPlatformMarkSvg(props) {
  const reactId = useId().replace(/:/g, '');
  const filterId = `platform-slat-shade-${reactId}`;

  const rects = buildPlatformSlatRects({
    width: VB,
    height: VB,
    cx: VB / 2,
    maxW: 118,
    barH: 11.5,
    gap: 3.25,
    staggerScale: 1,
  });

  return (
    <svg viewBox={`0 0 ${VB} ${VB}`} xmlns="http://www.w3.org/2000/svg" {...props}>
      <defs>
        <filter
          id={filterId}
          x="-25%"
          y="-25%"
          width="150%"
          height="150%"
          colorInterpolationFilters="sRGB"
        >
          <feDropShadow dx="0" dy="1.25" stdDeviation="1.15" floodColor="#0f172a" floodOpacity="0.14" />
        </filter>
      </defs>
      <g filter={`url(#${filterId})`}>
        {rects.map((r, i) => (
          <rect
            key={`slat-${i}`}
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            rx={r.rx}
            ry={r.rx}
            fill="currentColor"
            fillOpacity={r.opacity}
          />
        ))}
      </g>
    </svg>
  );
}
